import React from 'react';
import type { FC } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import type { DocNode } from '@atlaskit/adf-schema';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';

import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import { type Snippet } from '../snippetTypes.ts';
import { getSuggestedDuplicateName, wrapAdfWithDocNode } from '../snippetUtils.ts';
import { useSnippetsActions, useSnippetsState } from '../store/snippetsStore.ts';
import {
	ANALYTICS_OPERATIONAL_SUBJECT,
	ANALYTICS_ORIGIN,
	ANALYTICS_UI_SUBJECT_ID,
	MODAL_OUTCOME,
	MODAL_TYPE,
} from '../constants/snippetAnalytics.ts';
import { useOpenSnippetPanel } from '../hooks/useOpenSnippetPanel.ts';
import { useSnippetAnalytics } from '../hooks/useSnippetAnalytics.ts';

import { getSnippetBaseModalProps, SnippetBaseModal } from './SnippetBaseModal.tsx';
import { messages as baseMessages } from './SnippetBaseModal.i18n.ts';
import { isBodyTooLargeError } from '../services/blockServiceTypes.ts';
import type { SnippetBaseModalFormValues } from './snippetModalFormUtils.ts';
import { getDefaultFormValues } from './snippetModalFormUtils.ts';
import { useSnippetModalClose } from './useSnippetModalClose.ts';

export const duplicateSnippetModalMessages = defineMessages({
	titleDuplicate: {
		id: 'snippets.snippetEditorModal.titleDuplicate',
		defaultMessage: 'Create duplicate of {snippetName}',
		description: 'Title for the duplicate block template modal',
	},
	duplicateButton: {
		id: 'snippets.snippetEditorModal.duplicateButton',
		defaultMessage: 'Duplicate',
		description: 'Duplicate button text for duplicate mode',
	},
	duplicateSuccessFlagTitle: {
		id: 'snippets.snippetEditorModal.duplicateSuccessFlagTitle',
		defaultMessage: 'Template "{snippetName}" duplicated',
		description: 'Success flag title shown when a block template has been duplicated',
	},
	duplicateErrorFlagTitle: {
		id: 'snippets.snippetEditorModal.duplicateErrorFlagTitle',
		defaultMessage: 'Could not duplicate the template',
		description: 'Error flag title shown when a block template duplication fails',
	},
	duplicateErrorFlagDescription: {
		id: 'snippets.snippetEditorModal.duplicateErrorFlagDescription',
		defaultMessage: 'Please try again',
		description: 'Error flag description shown when a block template duplication fails',
	},
});

export type DuplicateSnippetModalProps = {
	snippet: Snippet;
	onClose: () => void;
	onCloseComplete: () => void;
	contentId: string;
	spaceId: string;
	spaceKey: string;
	editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
} & WithFlagsProps;

const DuplicateSnippetModalWithFlags: FC<DuplicateSnippetModalProps> = ({
	snippet,
	onClose,
	onCloseComplete,
	contentId,
	spaceId,
	spaceKey,
	flags,
	editorAPI,
}) => {
	const intl = useIntl();
	const openSnippetPanel = useOpenSnippetPanel();
	const { isSnippetSharingForSiteEnabled = false } = useSnippetsState();
	const snippetsActions = useSnippetsActions();
	const { fireFailedAnalytic, fireSuccessAnalytic } = useSnippetAnalytics(
		ANALYTICS_ORIGIN.DUPLICATE_SNIPPET_MODAL,
	);
	const handleClose = useSnippetModalClose(
		onClose,
		ANALYTICS_ORIGIN.DUPLICATE_SNIPPET_MODAL,
		ANALYTICS_UI_SUBJECT_ID.DUPLICATE_SNIPPET,
	);

	const baseValues = getDefaultFormValues(isSnippetSharingForSiteEnabled);
	const suggestedName = getSuggestedDuplicateName(snippet.name ?? '');
	const defaultValues: SnippetBaseModalFormValues = {
		...baseValues,
		snippetName: suggestedName,
		snippetDescription: snippet.description ?? '',
	};

	const initialAdf = wrapAdfWithDocNode(snippet?.body);
	const formKey = `duplicate-${snippet.snippetId}`;
	const title = intl.formatMessage(duplicateSnippetModalMessages.titleDuplicate, {
		snippetName: snippet.name ?? '',
	});
	const submitLabel = intl.formatMessage(duplicateSnippetModalMessages.duplicateButton);

	const successFlagActions = [
		{
			content: intl.formatMessage(baseMessages.successFlagViewTemplateAction),
			onClick: openSnippetPanel,
		},
	];

	const onSubmit = async (values: SnippetBaseModalFormValues, currentAdf: DocNode) => {
		const bodySize = JSON.stringify(currentAdf).length;
		const duplicatedSnippet = await snippetsActions.createSnippet(
			values.snippetName,
			currentAdf,
			values.snippetDescription,
			{
				flags,
				intl,
				editorAPI: editorAPI ?? undefined,
				isShared: values.isShared,
				successFlagActions,
				onShareFailure: (snippetId) =>
					fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_SHARE, { snippetId }),
				onSaveFailure: (error) => {
					const isBodyTooLarge = isBodyTooLargeError(error);
					fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_DUPLICATE, {
						snippetId: snippet.snippetId,
						...(isBodyTooLarge && { isBodyTooLarge: true, bodySize }),
					});
				},
				messages: {
					successFlagTitle: duplicateSnippetModalMessages.duplicateSuccessFlagTitle,
					errorFlagTitle: duplicateSnippetModalMessages.duplicateErrorFlagTitle,
					errorFlagDescription: duplicateSnippetModalMessages.duplicateErrorFlagDescription,
				},
			},
		);
		if (duplicatedSnippet) {
			fireSuccessAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_SAVE_SUCCESS, {
				snippetId: duplicatedSnippet.snippetId,
				bodySize,
				modalType: MODAL_TYPE.DUPLICATE,
			});
			handleClose({
				outcome: MODAL_OUTCOME.DUPLICATED,
				snippetId: duplicatedSnippet.snippetId,
			});
		}
	};

	return (
		<SnippetBaseModal
			{...getSnippetBaseModalProps({
				title,
				submitLabel,
				formKey,
				defaultValues,
				initialAdf,
				onSubmit,
				handleClose,
				fireFailedAnalytic,
				onCloseComplete,
				contentId,
				spaceId,
				spaceKey,
				flags,
				isSnippetSharingForSiteEnabled,
			})}
		/>
	);
};

export const DuplicateSnippetModal = withFlags(DuplicateSnippetModalWithFlags);
